import { ErrorHandler, inject } from '@angular/core';
import { createErrorHandler } from '@sentry/angular';
import { LogService } from 'shared/core/data-access/log';
import { ToastService } from 'shared/design-system/data-access/toast';
import { NormalizedError } from '../../models/normalized-error/normalized-error.model';
import { SENTRY_CONFIG } from '../../providers/sentry-config/sentry-config.token';

export class ErrorHandlerService implements ErrorHandler {
    #toastService = inject(ToastService);
    #logService = inject(LogService);
    #sentryConfig = inject(SENTRY_CONFIG, { optional: true });
    #sentryErrorHandler = createErrorHandler();

    handleError(error: unknown) {
        this.showToast(error, {
            debugMode: !!this.#sentryConfig?.debugToast,
        });

        if (this.#sentryConfig?.dsn) {
            this.#sentryErrorHandler.handleError(error);
        } else {
            this.#logService.error(error);
        }
    }

    showToast = (error: unknown, options: { debugMode: boolean }) => {
        if (options.debugMode) {
            this.#showDebugToast(error);
        } else {
            this.#showToast(error);
        }
    };

    #showToast = (error: unknown) => {
        if (error instanceof NormalizedError) {
            this.#toastService.error({
                title: error.message,
                message: '',
            });
        }
    };

    #showDebugToast = (error: unknown) => {
        if (error instanceof NormalizedError) {
            this.#toastService.error({
                title: `[debug] ${error.message}`,
                message:
                    error.cause instanceof Error ? error.cause.message : '',
            });
        } else {
            this.#toastService.error({
                title: '[debug] Unhandled error',
                message:
                    error instanceof Object && 'message' in error
                        ? JSON.stringify(error.message)
                        : '',
            });
        }
    };
}
